import { StyleSheet, css } from 'aphrodite';
import { type FC, useEffect, useLayoutEffect, useRef } from 'react';

import type { CoachMessage } from '../ai';
import { Composer } from './Composer';
import { IconPlus } from './icons';
import { MessageBlock } from './MessageBlock';

/** How close to the bottom (in px) counts as "the user is following along". */
const NEAR_BOTTOM_PX = 100;

export type AIChatPanelProps = {
	messages: CoachMessage[];
	pinnedIds: string[];
	onTogglePin: (id: string) => void;
	isResponding: boolean;
	onSend: (text: string) => void;
	onNewConversation?: () => void;
};

/**
 * AI conversation surface for the AppDrawer. The 3-column layout shed its
 * right-hand chat column when the drawer overlays kicked in (4-col blow-up);
 * the same thread + composer now live inside a drawer tab so the lead view
 * stays at 2 columns and the chat is hidden behind a toolbar button.
 */
export const AIChatPanel: FC<AIChatPanelProps> = ({
	messages,
	pinnedIds,
	onTogglePin,
	isResponding,
	onSend,
	onNewConversation,
}) => {
	const threadRef = useRef<HTMLDivElement>(null);
	const wasAtBottomRef = useRef(true);

	useLayoutEffect(() => {
		const el = threadRef.current;
		if (!el) return;
		wasAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
	});

	useEffect(() => {
		const el = threadRef.current;
		if (!el) return;
		if (!wasAtBottomRef.current) return;
		el.scrollTop = el.scrollHeight;
	}, [messages.length]);

	return (
		<aside className={css(styles.panel)}>
			<header className={css(styles.header)}>
				<div className={css(styles.title)}>AI Conversation</div>
				{onNewConversation && (
					<button
						type='button'
						onClick={onNewConversation}
						title='Start a new conversation'
						aria-label='Start a new conversation'
						className={css(styles.newBtn)}
					>
						<IconPlus size={14} sw={2} />
					</button>
				)}
			</header>
			<div ref={threadRef} className={css(styles.thread)}>
				{messages.map(m => (
					<MessageBlock key={m.id} msg={m} isPinned={pinnedIds.includes(m.id)} onTogglePin={() => onTogglePin(m.id)} />
				))}
			</div>
			<footer className={css(styles.footer)}>
				<Composer onSend={onSend} disabled={isResponding} />
			</footer>
		</aside>
	);
};

const styles = StyleSheet.create({
	panel: {
		display: 'flex',
		flexDirection: 'column',
		minWidth: 0,
		minHeight: 0,
		height: '100%',
		background: 'var(--bg)',
	},
	header: {
		padding: '12px 16px',
		borderBottom: '1px solid var(--border)',
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 8,
	},
	title: {
		fontSize: 11,
		fontWeight: 600,
		letterSpacing: '0.08em',
		textTransform: 'uppercase',
		color: 'var(--ink-faint)',
	},
	newBtn: {
		width: 26,
		height: 26,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 999,
		background: 'transparent',
		color: 'var(--ink-muted)',
		border: '1px solid var(--border)',
		cursor: 'pointer',
		transition: 'background 120ms, color 120ms',
		':hover': {
			background: 'var(--hover)',
			color: 'var(--ink)',
		},
	},
	thread: {
		flex: 1,
		overflowY: 'auto',
		minHeight: 0,
		padding: '12px 14px',
		display: 'flex',
		flexDirection: 'column',
		gap: 10,
	},
	footer: {
		borderTop: '1px solid var(--border)',
		flexShrink: 0,
	},
});
